import {
  Alert,
  Button,
  LinearProgress,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useContext, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { ShopLocationType } from '../../../../interfaces/delivery-company/pickups';
import { IPaymentTask } from '../../../../interfaces/delivery-company/tasks';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import { displayLocation } from '../../../../utils/locations';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import InputWithLegend from '../../../common/input-with-legend/input-with-legend';
import { isMobile, isTablet } from 'react-device-detect';
import { IBranchInvoice } from '../../../../interfaces/common/branch-invoices';

interface CreateBranchPaymentTaskProps {
  invoice: IBranchInvoice;
  onCreated?: (task: IPaymentTask) => void;
}

function CreateBranchPaymentTask({ invoice, onCreated }: CreateBranchPaymentTaskProps) {
  const { config } = useContext(AuthContext);

  const [driver, setDriver] = useState<DriverType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');

  const handleFetchDrivers = async (query: string): Promise<DriverType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/drivers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: DriverType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    
    setIsLoading(true);
    setResponse(undefined);
    setLoadingMessage('Scheduling Payment...');
    setMessage('');

    const requestData = {
      branch_invoice: invoice.id,
      driver: driver,
    };

    try {
      const response = await axios.post<IPaymentTask>(
        `${API_ENDPOINT}/api/v1/payment-tasks/`,
        requestData,
        config
      );
      setResponse(response);
      setMessage('Payment scheduled successfully');
      onCreated && onCreated(response.data);
    } catch (e: any) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        if (e.response !== undefined)
          setMessage('Failed to schedule payment: ' + e.response.statusText);
        else setMessage('Failed to schedule payment');
      } else {
        setMessage('Failed to schedule payment');
      }
      console.log(e);
    }
    setIsLoading(false);
    setLoadingMessage('');
  };

  return (
    <>
      <Typography
        variant="h4"
        component="h4"
        sx={{ textAlign: 'center', mt: 2, mb: 2 }}
      >
        Schedule Branch Transfer Payment
      </Typography>

      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message !== '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
              sx={{ mb: 2 }}
            >
              {message}
              {response && response.status >= 300 && (
                <ErrorsFlattenner rawErrors={response.data} />
              )}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <InputWithLegend
              label={'Payment To'}
              key="pay-to"
              icon={<LocationOnIcon color="primary" />}
            >
              <Grid container spacing={2} key="branch">
                <Grid xs={12} md={12}>
                  <Typography variant="body1">
                    <b>From Branch:</b> {invoice.from_branch.name}
                  </Typography>
                  <Typography variant="body1">
                    <b>To Branch:</b> {invoice.to_branch.name}
                  </Typography>
                  <Typography variant="body1">
                    <b>Invoice #{invoice.id}</b> -{' '}
                    {new Date(invoice.created_at).toLocaleString()}
                  </Typography>
                </Grid>

                <Grid xs={12} md={12}>
                  <AutocompleteWithThrottle
                    label={'Assign Driver (Optional)'}
                    required={false}
                    fetchAllOnLoad
                    fetch={handleFetchDrivers}
                    value={driver}
                    getOptionLabel={(option: DriverType) =>
                      option.name + ' ' + option.phone_number
                    }
                    onChange={(e, value) => {
                      setDriver(value);
                    }}
                  />
                </Grid>
              </Grid>
            </InputWithLegend>

            <br />

            

            
            <div style={{ width: '100%', textAlign: 'center' }}>
              <Button
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                type="submit"
                variant="contained"
              >
                Schedule Payment
              </Button>
            </div>
          </form>
        </>
      )}
    </>
  );
}

export default CreateBranchPaymentTask;


